/**
 * POI + polygon layers for Leaflet image map (normalized [x,y] in [0,1], top-left origin).
 */
import L from "leaflet";
import { normalizedToLeafletLatLng } from "./mapImageCoords.js";
import {
  buildNumericPinMarkerHtml,
  normalizeNumericPoiMarkerStyle,
  getNumericPinIconSizePx,
  getNumericMarkerIconSizePx,
  applyLeafletMarkerHoverScale,
  applyLeafletNumericMarkerVisualHover,
  numericMarkerHoverScale,
  wrapScalableMarkerHtml,
  getNumericPoiTooltipOffsetYFromMarker,
  runDuringNumericMarkerScaleTransition,
} from "./numericPoiMarker.js";
import { numericPoiMarkerNumber } from "./numericPoiOrder.js";

const GROUP_KEY = "_coveImageTourLayerGroup";

/** Touch-primary viewports: Leaflet's `click` on markers is unreliable on iOS Safari. */
function prefersTouchTap() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(pointer: coarse)").matches ||
    window.matchMedia("(hover: none)").matches
  );
}

/**
 * `touchend` on touch devices (with passive: false), `click` on fine-pointer / hover desktops.
 */
function bindMarkerTap(map, marker, onActivate) {
  const fire = (e) => {
    if (e) L.DomEvent.stopPropagation(e);
    const p = map.latLngToContainerPoint(marker.getLatLng());
    onActivate({ x: p.x, y: p.y });
  };
  if (prefersTouchTap()) {
    const onTouchEnd = (e) => {
      e.preventDefault();
      L.DomEvent.stopPropagation(e);
      fire(e);
    };
    const bindIcon = () => {
      const el = marker._icon;
      if (!el || el._coveTapBound) return;
      el._coveTapBound = true;
      el.addEventListener("touchend", onTouchEnd, { passive: false });
    };
    marker.on("add", bindIcon);
    bindIcon();
  } else {
    marker.on("click", fire);
  }
}

function bindPathTap(map, pathLayer, onActivate) {
  const fireAt = (latlng) => {
    if (!latlng) return;
    const p = map.latLngToContainerPoint(latlng);
    onActivate({ x: p.x, y: p.y });
  };
  if (prefersTouchTap()) {
    const onTouchEnd = (e) => {
      e.preventDefault();
      L.DomEvent.stopPropagation(e);
      const containerPoint = L.DomEvent.getMousePosition(e, map.getContainer());
      fireAt(map.containerPointToLatLng(containerPoint));
    };
    const bindPath = () => {
      const el = pathLayer._path || (typeof pathLayer.getElement === "function" ? pathLayer.getElement() : null);
      if (!el || el._coveTapBound) return;
      el._coveTapBound = true;
      el.addEventListener("touchend", onTouchEnd, { passive: false });
    };
    pathLayer.on("add", bindPath);
    bindPath();
  } else {
    pathLayer.on("click", (e) => {
      L.DomEvent.stopPropagation(e);
      fireAt(e.latlng);
    });
  }
}

function iconifySvgUrl(iconifyId, fillHex) {
  const idx = iconifyId.indexOf(":");
  if (idx <= 0) return "";
  const prefix = iconifyId.slice(0, idx);
  const name = iconifyId.slice(idx + 1);
  if (!prefix || !name) return "";
  const hex = String(fillHex).replace("#", "");
  return `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(name)}.svg?color=%23${encodeURIComponent(hex)}`;
}

const POI_ICON_MIN = 16;
const POI_ICON_MAX = 96;
const POI_ICON_DEFAULT_DOT = 22;
const POI_ICON_DEFAULT_MARKER = 28;

function clampPoiIconSizePx(n) {
  if (!Number.isFinite(n)) return POI_ICON_DEFAULT_MARKER;
  return Math.min(POI_ICON_MAX, Math.max(POI_ICON_MIN, Math.round(n)));
}

function getPoiIconSizePx(poi, variant) {
  const raw = poi && poi.iconSize;
  if (raw != null && Number.isFinite(Number(raw))) {
    return clampPoiIconSizePx(Number(raw));
  }
  return variant === "dot" ? POI_ICON_DEFAULT_DOT : POI_ICON_DEFAULT_MARKER;
}

/** Darken / lighten for hover when `hoverColor` is not set (matches mapPois.js). */
function getHoverColor(color) {
  if (!color || typeof color !== "string" || !/^#[0-9a-fA-F]{6}$/.test(color))
    return color;
  const colorInt = parseInt(color.slice(1), 16);
  if (Number.isNaN(colorInt)) return color;
  const isDark = colorInt > 0xffffff / 2;
  const factor = isDark ? 0.9 : 1.1;
  const newColorInt = Math.round(colorInt * factor);
  return "#" + Math.min(0xffffff, newColorInt).toString(16).padStart(6, "0");
}

/**
 * @param {object} poi
 * @param {boolean} isNumeric
 * @param {number} num 1-based index for numeric POIs
 * @param {string} themeBg
 * @param {string} themeFg poiTextColor / numericPoiTextColor — same as Mapbox circle-stroke (not hoverColor)
 * @param {{ hover?: boolean }} [opts]
 */
function makePoiIconFromPoi(poi, isNumeric, num, themeBg, themeFg, opts = {}) {
  const hover = opts.hover === true;
  const markerStyle = normalizeNumericPoiMarkerStyle(poi.icon);
  const baseBg =
    poi.bgColor && String(poi.bgColor).trim() ? poi.bgColor : themeBg;
  const bg = hover
    ? (poi.hoverColor && String(poi.hoverColor).trim()
        ? poi.hoverColor
        : getHoverColor(baseBg))
    : baseBg;
  const strokeColor =
    (themeFg && String(themeFg).trim()) || "rgba(255,255,255,0.9)";

  if (isNumeric) {
    if (markerStyle === "pin") {
      const size = getNumericPinIconSizePx(poi);
      const inner = buildNumericPinMarkerHtml({ pinColor: bg, number: num, size });
      const html = wrapScalableMarkerHtml(inner, {
        baseSize: size,
        transformOrigin: `${size / 2}px ${size}px`,
      });
      return L.divIcon({
        className: "cove-image-map-poi",
        html,
        iconSize: [size, size],
        iconAnchor: [size / 2, size],
      });
    }
    const size = getPoiIconSizePx(poi, "marker");
    const fontPx = Math.max(10, Math.round(size * (12 / 28)));
    const inner = `<div style="min-width:${size}px;height:${size}px;padding:0 6px;border-radius:9999px;background:${bg};color:${themeFg};display:flex;align-items:center;justify-content:center;font-size:${fontPx}px;font-weight:600;border:2px solid ${strokeColor};box-shadow:0 1px 3px rgba(0,0,0,0.35);touch-action:manipulation;-webkit-user-select:none;user-select:none;">${num}</div>`;
    const html = wrapScalableMarkerHtml(inner, {
      baseSize: size,
      transformOrigin: "center center",
    });
    return L.divIcon({
      className: "cove-image-map-poi",
      html,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  const rawIcon = (poi.icon && String(poi.icon).trim()) || "default";

  if (rawIcon.includes(":")) {
    const url = iconifySvgUrl(rawIcon, bg.replace("#", ""));
    if (url) {
      const size = getPoiIconSizePx(poi, "marker");
      const html = `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.35));touch-action:manipulation;-webkit-user-select:none;user-select:none;"><img src="${url}" draggable="false" width="${size}" height="${size}" alt="" style="width:${size}px;height:${size}px;min-width:${size}px;min-height:${size}px;display:block;object-fit:contain;pointer-events:none;"/></div>`;
      return L.divIcon({
        className: "cove-image-map-poi",
        html,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    }
  }

  if (rawIcon === "pin" || rawIcon.startsWith("accessibility-")) {
    const size = getPoiIconSizePx(poi, "marker");
    const src = `./images/accessibility/${rawIcon}.svg`;
    const html = `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.35));touch-action:manipulation;-webkit-user-select:none;user-select:none;"><img src="${src}" draggable="false" width="${size}" height="${size}" alt="" style="width:${size}px;height:${size}px;min-width:${size}px;min-height:${size}px;display:block;object-fit:contain;pointer-events:none;"/></div>`;
    return L.divIcon({
      className: "cove-image-map-poi",
      html,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  const size = getPoiIconSizePx(poi, "dot");
  const html = `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${bg};border:2px solid ${strokeColor};box-shadow:0 1px 3px rgba(0,0,0,0.35);touch-action:manipulation;-webkit-user-select:none;user-select:none;"></div>`;
  return L.divIcon({
    className: "cove-image-map-poi",
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function hexToRgbaFill(color) {
  if (!color || typeof color !== "string") return "rgba(0,0,0,0.25)";
  if (/^#[0-9a-fA-F]{8}$/.test(color)) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const a = parseInt(color.slice(7, 9), 16) / 255;
    return `rgba(${r},${g},${b},${a})`;
  }
  return color;
}

/**
 * @param {import('leaflet').Map} map
 * @param {object} opts
 */
export function addImageTourLayers(map, opts) {
  removeImageTourLayers(map);

  const group = L.layerGroup().addTo(map);
  map[GROUP_KEY] = group;

  const {
    tour,
    pois = [],
    numericPois = [],
    polygonPois = [],
    poiBgColor,
    poiTextColor,
    numericPoiBgColor,
    numericPoiTextColor,
    polygonPoiBgColor,
    setTooltip,
    onPoiClick,
    onNumericPoiClick,
    onPolygonPoiClick,
    scenes,
  } = opts;

  const bgPoi = poiBgColor || "#000000";
  const fgPoi = poiTextColor || "#ffffff";
  const bgNum = numericPoiBgColor || "#000000";
  const fgNum = numericPoiTextColor || "#ffffff";
  const polyThemeBg = polygonPoiBgColor || "#000000";
  const polyThemeFg = opts.polygonPoiTextColor || "#ffffff";
  let numericTooltipTransitionStop = null;
  let lastNumericTooltipEvent = null;

  pois.forEach((poi) => {
    if (poi.hidden) return;
    const loc = poi.location;
    if (!Array.isArray(loc) || loc.length < 2) return;
    const defaultIcon = makePoiIconFromPoi(poi, false, 0, bgPoi, fgPoi);
    const hoverIcon = makePoiIconFromPoi(poi, false, 0, bgPoi, fgPoi, {
      hover: true,
    });
    const m = L.marker(
      normalizedToLeafletLatLng(L, loc[0], loc[1], map._imageAspectWOverH ?? 1),
      { icon: defaultIcon },
    ).addTo(group);
    bindMarkerTap(map, m, (pt) => onPoiClick?.(poi, pt));
    m.on("mouseover", () => {
      m.setIcon(hoverIcon);
      applyLeafletMarkerHoverScale(m, poi, true);
      setTooltip?.({
        text: poi.title || "POI",
        x: map.latLngToContainerPoint(m.getLatLng()).x,
        y: map.latLngToContainerPoint(m.getLatLng()).y,
      });
    });
    m.on("mouseout", () => {
      m.setIcon(defaultIcon);
      applyLeafletMarkerHoverScale(m, poi, false);
      setTooltip?.(null);
    });
  });

  numericPois.forEach((poi, i) => {
    if (poi.hidden) return;
    const loc = poi.location;
    if (!Array.isArray(loc) || loc.length < 2) return;
    const num = numericPoiMarkerNumber(numericPois, i);
    const defaultIcon = makePoiIconFromPoi(poi, true, num, bgNum, fgNum);
    const hoverIcon = makePoiIconFromPoi(poi, true, num, bgNum, fgNum, {
      hover: true,
    });
    const m = L.marker(
      normalizedToLeafletLatLng(L, loc[0], loc[1], map._imageAspectWOverH ?? 1),
      { icon: defaultIcon },
    ).addTo(group);
    bindMarkerTap(map, m, (pt) => onNumericPoiClick?.(poi, pt));
    const baseBg =
      poi.bgColor && String(poi.bgColor).trim() ? poi.bgColor : bgNum;
    const hoverBg =
      poi.hoverColor && String(poi.hoverColor).trim()
        ? poi.hoverColor
        : getHoverColor(baseBg);
    const publishNumericTooltip = (domEvent) => {
      const p = domEvent
        ? map.mouseEventToContainerPoint(domEvent)
        : map.latLngToContainerPoint(m.getLatLng());
      setTooltip?.({
        text: poi.title || "POI",
        x: p.x,
        y: p.y,
        offsetY: getNumericPoiTooltipOffsetYFromMarker(poi, m),
      });
    };
    m.on("mouseover", (e) => {
      if (numericMarkerHoverScale(poi) != null) {
        applyLeafletNumericMarkerVisualHover(m, { baseBg, hoverBg }, true);
        applyLeafletMarkerHoverScale(m, poi, true);
      } else {
        m.setIcon(hoverIcon);
      }
      lastNumericTooltipEvent = e.originalEvent ?? null;
      numericTooltipTransitionStop?.();
      publishNumericTooltip(e.originalEvent);
      if (numericMarkerHoverScale(poi) != null) {
        numericTooltipTransitionStop = runDuringNumericMarkerScaleTransition(m, () => {
          publishNumericTooltip(lastNumericTooltipEvent);
        });
      }
    });
    m.on("mousemove", (e) => {
      if (!e.originalEvent) return;
      lastNumericTooltipEvent = e.originalEvent;
      publishNumericTooltip(e.originalEvent);
    });
    m.on("mouseout", () => {
      numericTooltipTransitionStop?.();
      numericTooltipTransitionStop = null;
      lastNumericTooltipEvent = null;
      if (numericMarkerHoverScale(poi) != null) {
        applyLeafletNumericMarkerVisualHover(m, { baseBg, hoverBg }, false);
        applyLeafletMarkerHoverScale(m, poi, false);
      } else {
        m.setIcon(defaultIcon);
      }
      setTooltip?.(null);
    });
  });

  polygonPois.forEach((poi) => {
    if (poi.hidden) return;
    const raw = poi.coordinates;
    if (!Array.isArray(raw) || raw.length < 3) return;
    const latlngs = raw.map(([nx, ny]) =>
      normalizedToLeafletLatLng(L, nx, ny, map._imageAspectWOverH ?? 1),
    );
    const fillBase =
      hexToRgbaFill(poi.fillColor || polyThemeBg) || "rgba(0,0,0,0.25)";
    const borderBase =
      hexToRgbaFill(poi.borderColor || polyThemeFg) || "#ffffff";
    const fillHover =
      hexToRgbaFill(
        poi.hoverFillColor || poi.fillColor || polyThemeBg,
      ) || fillBase;
    const borderHover =
      hexToRgbaFill(
        poi.hoverBorderColor || poi.borderColor || polyThemeFg,
      ) || borderBase;
    const weight = poi.borderWidth ?? 2;

    const poly = L.polygon(latlngs, {
      color: borderBase,
      weight,
      fillColor: fillBase,
      fillOpacity: 0.85,
    }).addTo(group);
    bindPathTap(map, poly, (pt) => onPolygonPoiClick?.(poi, pt));
    poly.on("mouseover", (e) => {
      poly.setStyle({
        fillColor: fillHover,
        color: borderHover,
        weight,
      });
      const p = map.latLngToContainerPoint(e.latlng);
      setTooltip?.({
        text: poi.title || "Area",
        x: p.x,
        y: p.y,
      });
    });
    poly.on("mouseout", () => {
      poly.setStyle({
        fillColor: fillBase,
        color: borderBase,
        weight,
      });
      setTooltip?.(null);
    });
  });
}

export function removeImageTourLayers(map) {
  const g = map[GROUP_KEY];
  if (g) {
    g.remove();
    map.removeLayer(g);
    delete map[GROUP_KEY];
  }
}
